import type { PreviewAnnotationPayload } from "@cleo/contracts";
import { buildElementContextBlock, normalizeElementContextSelection } from "./elementContext";

const TRAILING_PREVIEW_ANNOTATION_BLOCK_PATTERN =
  /\n*<preview_annotation>\n((?:(?!<preview_annotation>)[\s\S])*)\n<\/preview_annotation>\s*$/;

export interface ParsedPreviewAnnotation {
  id: string;
  title: string;
  comment: string;
  targetSummary: string;
  styleChanges: string[];
  hasScreenshot: boolean;
}

export interface ExtractedPreviewAnnotation {
  promptText: string;
  annotation: ParsedPreviewAnnotation | null;
}

export function buildPreviewAnnotationPrompt(annotation: PreviewAnnotationPayload): string {
  const lines = ["Preview annotation:"];
  lines.push(`Id: ${annotation.id}`);
  const title = annotation.pageTitle?.trim() || annotation.pageUrl.trim() || "Preview";
  lines.push(`Page: ${title}`);
  if (annotation.canvas) {
    // CCD17: a pick on an open Canvas board is a pointer, not access; the
    // Canvas still reaches Cleo only through the message's boundary.
    const canvas = annotation.canvas;
    if (canvas.page) {
      // Can 17: a pick on a page Canvas names the page file and the element.
      lines.push(
        `Canvas: ${canvas.canvasId} at revision ${canvas.sequence}, page file ${canvas.page.file}`,
      );
      lines.push(
        `Picked element: <${canvas.page.tagName}> whose visible text is ${JSON.stringify(canvas.page.text)}`,
      );
      lines.push(
        `Change only this element of ${canvas.page.file} unless the message asks otherwise, and save it as a new revision that keeps every other file and every other part of the page as it is. If more than one element shows this text, ask which one before changing anything.`,
      );
    } else {
      lines.push(
        `Canvas: ${canvas.canvasId} at revision ${canvas.sequence}, placement ${canvas.nodeId}`,
      );
      if (canvas.path) {
        lines.push(
          `Picked input: ${canvas.path}, whose saved value is ${JSON.stringify(canvas.value)}`,
        );
        lines.push(
          "Change only this input of this placement unless the message asks otherwise; keep every other value and key.",
        );
      } else {
        lines.push(
          canvas.candidates.length > 0
            ? `The picked text matches more than one input (${canvas.candidates.join(", ")}); ask which one before changing anything.`
            : "The picked element could not be attributed to one input; ask what to change before changing anything.",
        );
      }
    }
  }
  if (annotation.comment.trim()) lines.push(`Comment: ${annotation.comment.trim()}`);
  const targets: string[] = [];
  if (annotation.elements.length > 0) {
    targets.push(
      `${annotation.elements.length} selected element${annotation.elements.length === 1 ? "" : "s"}`,
    );
  }
  if (annotation.regions.length > 0) {
    targets.push(
      `${annotation.regions.length} marked region${annotation.regions.length === 1 ? "" : "s"}`,
    );
  }
  if (annotation.strokes.length > 0) {
    targets.push(
      `${annotation.strokes.length} drawing${annotation.strokes.length === 1 ? "" : "s"}`,
    );
  }
  if (targets.length > 0) lines.push(`Targets: ${targets.join(", ")}.`);
  if (annotation.styleChanges.length > 0) {
    lines.push("Requested visual changes:");
    for (const change of annotation.styleChanges) {
      lines.push(`- ${change.property}: ${change.previousValue || "(unset)"} → ${change.value}`);
    }
  }
  if (annotation.screenshot) {
    lines.push("The attached screenshot is the annotated preview crop.");
  }
  const elementContexts = annotation.elements
    .map((target) => normalizeElementContextSelection(target.element))
    .filter((context) => context !== null);
  const elementBlock = buildElementContextBlock(elementContexts);
  if (elementBlock) lines.push(elementBlock);
  return ["<preview_annotation>", ...lines, "</preview_annotation>"].join("\n");
}

export function appendPreviewAnnotationPrompt(
  prompt: string,
  annotation: PreviewAnnotationPayload,
): string {
  const annotationText = buildPreviewAnnotationPrompt(annotation);
  const trimmed = prompt.trim();
  return trimmed ? `${trimmed}\n\n${annotationText}` : annotationText;
}

export function extractTrailingPreviewAnnotation(prompt: string): ExtractedPreviewAnnotation {
  const match = TRAILING_PREVIEW_ANNOTATION_BLOCK_PATTERN.exec(prompt);
  if (!match) return { promptText: prompt, annotation: null };
  const body = match[1] ?? "";
  const lines = body.split("\n");
  const pageLine = lines.find((line) => line.startsWith("Page: "));
  const idLine = lines.find((line) => line.startsWith("Id: "));
  const commentLine = lines.find((line) => line.startsWith("Comment: "));
  const targetsLine = lines.find((line) => line.startsWith("Targets: "));
  // A Canvas pick has no drawn targets; its sent card names what was pointed at.
  const pickedElement = /^Picked element: (<[^>]+>) whose visible text is (".*")$/.exec(
    lines.find((line) => line.startsWith("Picked element: ")) ?? "",
  );
  const pickedInput = /^Picked input: [^,]+, whose saved value is (.*)$/.exec(
    lines.find((line) => line.startsWith("Picked input: ")) ?? "",
  );
  const pickedSummary = pickedElement
    ? `Pointed at ${pickedElement[1]} ${pickedElement[2]}`
    : pickedInput
      ? `Pointed at ${pickedInput[1]}`
      : "";
  const styleHeadingIndex = lines.indexOf("Requested visual changes:");
  const linesAfterStyleHeading = lines.slice(styleHeadingIndex + 1);
  const elementContextIndex = linesAfterStyleHeading.indexOf("<element_context>");
  const styleChanges =
    styleHeadingIndex < 0
      ? []
      : linesAfterStyleHeading
          .slice(0, elementContextIndex < 0 ? undefined : elementContextIndex)
          .filter((line) => line.startsWith("- "))
          .map((line) => line.slice(2));
  return {
    promptText: prompt.slice(0, match.index).replace(/\n+$/, ""),
    annotation: {
      id: idLine?.slice("Id: ".length).trim() || `${match.index}`,
      title: pageLine?.slice("Page: ".length).trim() || "Preview annotation",
      comment: commentLine?.slice("Comment: ".length).trim() || "",
      targetSummary: targetsLine?.slice("Targets: ".length).trim() || pickedSummary,
      styleChanges,
      hasScreenshot: body.includes("The attached screenshot is the annotated preview crop."),
    },
  };
}

export async function previewAnnotationScreenshotFile(
  annotation: PreviewAnnotationPayload,
): Promise<File | null> {
  if (!annotation.screenshot) return null;
  const response = await fetch(annotation.screenshot.dataUrl);
  const blob = await response.blob();
  return new File([blob], `preview-annotation-${annotation.id}.png`, {
    type: blob.type || "image/png",
  });
}
